import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, ChevronDown, ChevronRight, Star, Heart, Target, Zap } from 'lucide-react';

// Core Types for Mobile Interactions
export interface ChoiceOption {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  category?: string;
  value: any;
}

export interface MobileSelectConfig {
  title: string;
  description?: string;
  options: ChoiceOption[];
  multiSelect?: boolean;
  required?: boolean;
  maxSelections?: number;
  showIcons?: boolean;
  layout?: 'grid' | 'list' | 'compact';
}

// Mobile Choice Selector Component
export const MobileChoiceSelector: React.FC<{
  config: MobileSelectConfig;
  selected: string[];
  onSelectionChange: (selected: string[]) => void;
  className?: string;
}> = ({ config, selected, onSelectionChange, className = '' }) => {
  const [expanded, setExpanded] = useState(false);

  const handleOptionSelect = (optionId: string) => {
    if (config.multiSelect) {
      const newSelected = selected.includes(optionId)
        ? selected.filter(id => id !== optionId)
        : [...selected, optionId].slice(0, config.maxSelections || 10);
      onSelectionChange(newSelected);
    } else {
      onSelectionChange([optionId]);
      setExpanded(false);
    }
  };

  const selectedOptions = config.options.filter(opt => selected.includes(opt.id));

  return (
    <div className={`w-full ${className}`}>
      {/* Header with current selection */}
      <motion.div
        className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
        whileTap={{ scale: 0.98 }}
      >
        <div className="flex justify-between items-center">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">{config.title}</h3>
            {config.description && (
              <p className="text-sm text-gray-600 mt-1">{config.description}</p>
            )}
            {selectedOptions.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {selectedOptions.map(option => (
                  <Badge key={option.id} variant="secondary" className="text-xs">
                    {option.label}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <motion.div
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="w-5 h-5 text-gray-400" />
          </motion.div>
        </div>
      </motion.div>

      {/* Options Panel */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2"
          >
            <div className={`
              ${config.layout === 'grid' ? 'grid grid-cols-2 gap-2' : 'space-y-2'}
              ${config.layout === 'compact' ? 'flex flex-wrap gap-2' : ''}
            `}>
              {config.options.map(option => (
                <motion.div
                  key={option.id}
                  className={`
                    p-3 rounded-lg border cursor-pointer transition-all
                    ${selected.includes(option.id)
                      ? 'bg-blue-50 border-blue-200 text-blue-900'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                    }
                    ${config.layout === 'compact' ? 'px-3 py-2' : ''}
                  `}
                  onClick={() => handleOptionSelect(option.id)}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="flex items-center gap-2">
                    {config.showIcons && option.icon && (
                      <div className="flex-shrink-0">{option.icon}</div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${config.layout === 'compact' ? 'text-sm' : ''}`}>
                          {option.label}
                        </span>
                        {selected.includes(option.id) && (
                          <Check className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                      {option.description && config.layout !== 'compact' && (
                        <p className="text-xs text-gray-600 mt-1">{option.description}</p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Mobile Comparison Interface
export const MobileComparisonSelector: React.FC<{
  title: string;
  options: Array<{
    id: string;
    title: string;
    content: string;
    rating?: number;
    highlights?: string[];
  }>;
  onSelection: (selectedId: string) => void;
  showRatings?: boolean;
}> = ({ title, options, onSelection, showRatings = false }) => {
  const [selected, setSelected] = useState<string | null>(null);

  const handleSelect = (id: string) => {
    setSelected(id);
    onSelection(id);
  };

  return (
    <div className="w-full">
      <h3 className="font-semibold text-lg mb-4 text-center">{title}</h3>
      <div className="space-y-3">
        {options.map(option => (
          <motion.div
            key={option.id}
            className={`
              border rounded-lg p-4 cursor-pointer transition-all
              ${selected === option.id
                ? 'border-green-500 bg-green-50 shadow-md'
                : 'border-gray-200 hover:border-gray-300'
              }
            `}
            onClick={() => handleSelect(option.id)}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-medium text-gray-900">{option.title}</h4>
              {showRatings && option.rating && (
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < option.rating! ? 'text-yellow-400 fill-current' : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              )}
              {selected === option.id && (
                <Check className="w-5 h-5 text-green-600" />
              )}
            </div>
            <p className="text-sm text-gray-700 mb-2">{option.content}</p>
            {option.highlights && (
              <div className="flex flex-wrap gap-1">
                {option.highlights.map((highlight, i) => (
                  <Badge key={i} variant="outline" className="text-xs">
                    {highlight}
                  </Badge>
                ))}
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// Swipe Navigation Component
export const SwipeNavigator: React.FC<{
  items: Array<{ id: string; title: string; content: React.ReactNode }>;
  currentIndex: number;
  onIndexChange: (index: number) => void;
}> = ({ items, currentIndex, onIndexChange }) => {
  const [dragStart, setDragStart] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (event: any) => {
    setDragStart(event.clientX || event.touches?.[0]?.clientX || 0);
    setIsDragging(true);
  };

  const handleDragEnd = (event: any) => {
    if (!isDragging) return;
    
    const dragEnd = event.clientX || event.changedTouches?.[0]?.clientX || 0;
    const dragDistance = dragEnd - dragStart;
    const threshold = 50;

    if (dragDistance > threshold && currentIndex > 0) {
      onIndexChange(currentIndex - 1);
    } else if (dragDistance < -threshold && currentIndex < items.length - 1) {
      onIndexChange(currentIndex + 1);
    }

    setIsDragging(false);
  };

  return (
    <div className="relative overflow-hidden">
      <div className="flex items-center justify-center mb-4">
        <div className="flex gap-2">
          {items.map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex ? 'bg-blue-600 w-6' : 'bg-gray-300'
              }`}
              onClick={() => onIndexChange(index)}
            />
          ))}
        </div>
      </div>

      <motion.div
        className="flex"
        animate={{ x: `-${currentIndex * 100}%` }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        onMouseDown={handleDragStart}
        onMouseUp={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchEnd={handleDragEnd}
        style={{ width: `${items.length * 100}%` }}
      >
        {items.map((item, index) => (
          <div
            key={item.id}
            className="w-full flex-shrink-0 px-2"
            style={{ width: `${100 / items.length}%` }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-center">{item.title}</CardTitle>
              </CardHeader>
              <CardContent>
                {item.content}
              </CardContent>
            </Card>
          </div>
        ))}
      </motion.div>

      <div className="flex justify-between mt-4">
        <Button
          variant="outline"
          onClick={() => currentIndex > 0 && onIndexChange(currentIndex - 1)}
          disabled={currentIndex === 0}
        >
          Previous
        </Button>
        <Button
          onClick={() => currentIndex < items.length - 1 && onIndexChange(currentIndex + 1)}
          disabled={currentIndex === items.length - 1}
        >
          Next <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

// Touch-friendly Decision Tree
export const TouchDecisionTree: React.FC<{
  nodes: Array<{
    id: string;
    question: string;
    options: Array<{ label: string; nextId?: string; result?: any }>;
  }>;
  onComplete: (result: any) => void;
}> = ({ nodes, onComplete }) => {
  const [currentNodeId, setCurrentNodeId] = useState(nodes[0]?.id);
  const [path, setPath] = useState<string[]>([]);

  const currentNode = nodes.find(node => node.id === currentNodeId);

  const handleOptionSelect = (option: any) => {
    if (option.result) {
      onComplete(option.result);
    } else if (option.nextId) {
      setPath([...path, currentNodeId]);
      setCurrentNodeId(option.nextId);
    }
  };

  const goBack = () => {
    if (path.length > 0) {
      const previousId = path[path.length - 1];
      setPath(path.slice(0, -1));
      setCurrentNodeId(previousId);
    }
  };

  if (!currentNode) return null;

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          {path.length > 0 && (
            <Button variant="ghost" size="sm" onClick={goBack}>
              ← Back
            </Button>
          )}
          <div className="text-sm text-gray-500">
            Step {path.length + 1} of {nodes.length}
          </div>
        </div>
        <h3 className="text-lg font-semibold text-center mb-4">
          {currentNode.question}
        </h3>
      </div>

      <div className="space-y-3">
        {currentNode.options.map((option, index) => (
          <motion.button
            key={index}
            className="w-full p-4 bg-white border border-gray-200 rounded-lg text-left hover:bg-gray-50 transition-colors"
            onClick={() => handleOptionSelect(option)}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="flex items-center justify-between">
              <span className="font-medium">{option.label}</span>
              <ChevronRight className="w-4 h-4 text-gray-400" />
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

// PACE Framework Mobile Builder
export const PACEMobileBuilder: React.FC<{
  onComplete: (framework: any) => void;
}> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [framework, setFramework] = useState({
    purpose: '',
    audience: '',
    connection: '',
    engagement: ''
  });

  const steps = [
    {
      id: 'purpose',
      title: 'Purpose',
      icon: <Target className="w-5 h-5" />,
      color: 'bg-blue-500',
      question: 'What do you want to achieve?',
      options: [
        { id: 'inform', label: 'Inform & Update', description: 'Share important information' },
        { id: 'persuade', label: 'Persuade & Convince', description: 'Change minds or behavior' },
        { id: 'build', label: 'Build Relationships', description: 'Strengthen connections' },
        { id: 'solve', label: 'Solve Problems', description: 'Address specific issues' },
        { id: 'celebrate', label: 'Celebrate Success', description: 'Acknowledge achievements' }
      ]
    },
    {
      id: 'audience',
      title: 'Audience',
      icon: <Heart className="w-5 h-5" />,
      color: 'bg-green-500',
      question: 'Who are you communicating with?',
      options: [
        { id: 'executives', label: 'Busy Executives', description: 'Need quick, strategic insights' },
        { id: 'team', label: 'Team Members', description: 'Want clarity and direction' },
        { id: 'clients', label: 'External Clients', description: 'Value professionalism and results' },
        { id: 'stakeholders', label: 'Key Stakeholders', description: 'Seek transparency and updates' },
        { id: 'community', label: 'Community Members', description: 'Want involvement and connection' }
      ]
    },
    {
      id: 'connection',
      title: 'Connection',
      icon: <Heart className="w-5 h-5" />,
      color: 'bg-purple-500',
      question: 'How will you connect emotionally?',
      options: [
        { id: 'story', label: 'Personal Story', description: 'Share relatable experiences' },
        { id: 'examples', label: 'Real Examples', description: 'Use concrete illustrations' },
        { id: 'concerns', label: 'Address Concerns', description: 'Acknowledge their worries' },
        { id: 'appreciation', label: 'Show Gratitude', description: 'Express genuine thanks' },
        { id: 'values', label: 'Shared Values', description: 'Connect to common beliefs' }
      ]
    },
    {
      id: 'engagement',
      title: 'Engagement',
      icon: <Zap className="w-5 h-5" />,
      color: 'bg-orange-500',
      question: 'How will you drive action?',
      options: [
        { id: 'questions', label: 'Ask Questions', description: 'Prompt specific responses' },
        { id: 'steps', label: 'Clear Next Steps', description: 'Make action obvious' },
        { id: 'urgency', label: 'Create Urgency', description: 'Emphasize importance' },
        { id: 'interactive', label: 'Interactive Elements', description: 'Make it engaging' },
        { id: 'easy', label: 'Easy Response', description: 'Remove friction' }
      ]
    }
  ];

  const currentStepData = steps[currentStep];
  const stepKey = currentStepData.id as keyof typeof framework;

  const handleSelection = (optionId: string) => {
    const option = currentStepData.options.find(opt => opt.id === optionId);
    if (!option) return;

    const updatedFramework = {
      ...framework,
      [stepKey]: option.label
    };
    setFramework(updatedFramework);

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete(updatedFramework);
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="w-full max-w-lg mx-auto">
      {/* Progress Indicator */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${
                index === currentStep
                  ? step.color
                  : index < currentStep
                  ? 'bg-green-500'
                  : 'bg-gray-300'
              }`}
            >
              {step.icon}
            </div>
          ))}
        </div>
        <div className="text-sm text-gray-500">
          {currentStep + 1} of {steps.length}
        </div>
      </div>

      {/* Current Step */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white ${currentStepData.color}`}>
              {currentStepData.icon}
            </div>
            {currentStepData.title}
          </CardTitle>
          <p className="text-lg font-medium text-center">{currentStepData.question}</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {currentStepData.options.map((option) => (
              <motion.button
                key={option.id}
                className="w-full p-4 bg-white border border-gray-200 rounded-lg text-left hover:bg-gray-50 hover:border-gray-300 transition-all"
                onClick={() => handleSelection(option.id)}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-medium text-gray-900">{option.label}</div>
                    <div className="text-sm text-gray-600">{option.description}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </div>
              </motion.button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between mt-4">
        <Button variant="outline" onClick={goBack} disabled={currentStep === 0}>
          Back
        </Button>
        <div className="text-sm text-gray-500 self-center">
          Tap an option to continue
        </div>
      </div>
    </div>
  );
};

export default {
  MobileChoiceSelector,
  MobileComparisonSelector,
  SwipeNavigator,
  TouchDecisionTree,
  PACEMobileBuilder
};